import { BaseAdapter } from './BaseAdapter'
import { ACCESS_TOKEN } from '../assets/constants'
import {
	PlaceOrModifyOrdersRequest,
	PlaceOrModifyOrdersResponse,
	GetAllOrdersRequest,
	CancelOrdersRequest,
	CancelOrdersResponse,
	GetHoldingsResponse,
	Holding,
	TradingAccountMargin,
	GetTradingAccountMarginRequest
} from '../proto/generated/trading-bridge'
import { MarginFromBrokerRequest, MarginFromBrokerResponse, Order, Orders } from '../proto/generated/order'
import { Security } from '../proto/generated/security'
import { PositionRequest, PositionResponse } from '../proto/generated/position'
import {
	AuthProviderLoginUrlResponse,
	ForgetPasswordRequest,
	GetActiveSessionsResponse,
	LoginRequest,
	LoginResponse,
	LoginWithMobileRequest,
	LoginWithMobileResponse,
	OperationResponse,
	RevokeSessionRequest,
	User,
	UserSignUpRequest,
	ValidateThirdPartyTokenRequest,
	VerifyMobileLoginRequest
} from '../proto/generated/identity'
import api from '../services/apis/axiosClient'
import { clearAuthSession } from '../services/auth/tokenRefresh'

export class Tick2TradeAdapter extends BaseAdapter {
	genrateLocalSecurityMap(_security: Security): void {}

	transformRequest<T = any>(
		endpoint: string,
		data: T,
		method: string
	): {
		url: string
		data: any
		headers?: Record<string, string>
	} {
		return {
			url: endpoint,
			data: data,
			headers: this.getCustomHeaders()
		}
	}

	transformResponse<T = any>(response: any, endpoint: string): T {
		return response as T
	}

	transformError(error: any): any {
		return super.transformError(error)
	}

	private getAuthHeaders() {
		const token = localStorage.getItem(ACCESS_TOKEN)
		return {
			'Authorization': `Bearer ${token}`,
			'Content-Type': 'application/json',
			'Accept': 'application/x-protobuf'
		}
	}

	async placeOrModifyOrder(request: PlaceOrModifyOrdersRequest): Promise<PlaceOrModifyOrdersResponse> {
		request.order.forEach(o => {
			o.security = {
				exchange: o.security.exchange,
				code: o.security.code
			}
		})

		const response = await api.post(
			`/orders/place`,
			JSON.stringify(request),
			{
				headers: this.getAuthHeaders(),
				responseType: 'arraybuffer'
			}
		)

		return PlaceOrModifyOrdersResponse.decode(new Uint8Array(response.data))
	}

	async getOrders(request: GetAllOrdersRequest): Promise<Order[]> {
		const response = await api.post(
			`/orders/all`,
			JSON.stringify(request),
			{
				headers: this.getAuthHeaders(),
				responseType: 'arraybuffer'
			}
		)

		const responseData = Orders.decode(new Uint8Array(response.data))
		return responseData.orders
	}

	async cancelOrder(request: CancelOrdersRequest): Promise<CancelOrdersResponse> {
		const response = await api.post(
			`/orders/cancel`,
			JSON.stringify(request),
			{
				headers: this.getAuthHeaders(),
				responseType: 'arraybuffer'
			}
		)

		return CancelOrdersResponse.decode(new Uint8Array(response.data))
	}

	async getPositions(request: PositionRequest): Promise<PositionResponse> {
		const response = await api.post(
			`/positions`,
			JSON.stringify(request),
			{
				headers: this.getAuthHeaders(),
				responseType: 'arraybuffer'
			}
		)

		return PositionResponse.decode(new Uint8Array(response.data))
	}

	async getPlacedOrder(params: any): Promise<Order> {
		const response = await api.get(
			`/orders`,
			{
				headers: this.getAuthHeaders(),
				params,
				responseType: 'arraybuffer'
			}
		)

		return Order.decode(new Uint8Array(response.data))
	}

	async getHoldings(accountId: string | undefined): Promise<Holding[]> {
		try {
			const response = await api.get('/orders/holdings', {
				params: {
					accountId: accountId,
				},
				headers: {
					'Accept': 'application/x-protobuf',
				},
				responseType: 'arraybuffer',
			})
			const responseData = GetHoldingsResponse.decode(new Uint8Array(response.data))
			return responseData?.holdings ?? []
		} catch (error: any) {
			console.error('Error fetching holdings:', error)
			throw error
		}
	}

	async syncTradingAccountMargin(request: GetTradingAccountMarginRequest): Promise<TradingAccountMargin> {
		return new Promise((resolve, reject) => {
			const url = `/trading-accounts/sync-margin`
			api.post(url, JSON.stringify(request), {
				headers: {
					'Accept': 'application/x-protobuf'
				},
				responseType: 'arraybuffer'
			}).then(async (response) => {
				const decodedData = TradingAccountMargin.decode(new Uint8Array(response.data))
				resolve(decodedData)
			}).catch((error) => {
				reject(error)
			})
		})
	}

	async getRequiredMargin(request: MarginFromBrokerRequest): Promise<MarginFromBrokerResponse> {
		return new Promise((resolve, reject) => {
			const url = `trading-accounts/calculate-margin`
			api.post(url, JSON.stringify(request), {
				headers: {
					'Accept': 'application/x-protobuf'
				},
				responseType: 'arraybuffer'
			}).then(async (response) => {
				const decodedData = MarginFromBrokerResponse.decode(new Uint8Array(response.data))
				resolve(decodedData)
			}).catch((error) => {
				reject(error)
			})
		})
	}

	async getLoginUrl(body: any): Promise<AuthProviderLoginUrlResponse> {
		const response = await api.post(
			`/auth/login-url`,
			JSON.stringify(body),
			{
				headers: {
					'Content-Type': 'application/json',
					'Accept': 'application/x-protobuf'
				},
				responseType: 'arraybuffer'
			}
		)

		return AuthProviderLoginUrlResponse.decode(new Uint8Array(response.data))
	}

	async loginWithMail(body: LoginRequest): Promise<LoginResponse> {
		const response = await api.post(
			`/auth/login`,
			JSON.stringify(body),
			{
				headers: {
					'Content-Type': 'application/json',
					'Accept': 'application/x-protobuf',
					'X-Suppress-Error': 'true',
				},
				responseType: 'arraybuffer'
			}
		)

		return LoginResponse.decode(new Uint8Array(response.data))
	}

	async loginWithMobile(body: LoginWithMobileRequest): Promise<LoginWithMobileResponse> {
		const response = await api.post(
			`/auth/login-with-mobile`,
			JSON.stringify(body),
			{
				headers: {
					'Content-Type': 'application/json',
					'Accept': 'application/x-protobuf',
					'X-Suppress-Error': 'true',
				},
				responseType: 'arraybuffer'
			}
		)
		return LoginWithMobileResponse.decode(new Uint8Array(response.data))
	}

	async verifyMobileLogin(body: VerifyMobileLoginRequest): Promise<LoginResponse> {
		const response = await api.post(
			`/auth/verify-mobile-login`,
			JSON.stringify(body),
			{
				headers: {
					'Content-Type': 'application/json',
					'Accept': 'application/x-protobuf',
					'X-Suppress-Error': 'true',
				},
				responseType: 'arraybuffer'
			}
		)
		return LoginResponse.decode(new Uint8Array(response.data))
	}

	async signUpWithMail(userSignUpRequest: UserSignUpRequest): Promise<LoginResponse> {
		const response = await api.post(
			`/user/email-signup`,
			JSON.stringify(userSignUpRequest),
			{
				headers: {
					'Content-Type': 'application/json',
					'Accept': 'application/x-protobuf'
				},
				responseType: 'arraybuffer'
			}
		)

		return LoginResponse.decode(new Uint8Array(response.data))
	}

	async sendEmailOtp(email: string, options?: { purpose?: string }): Promise<any> {
		const response = await api.get(
			`/user/send-email-otp`,
			{
				headers: {
					'Accept': 'application/json'
				},
				params: {
					email,
					...(options?.purpose ? { purpose: options.purpose } : {}),
				},
				responseType: 'json'
			}
		)

		return response
	}

	async forgetPassword(body: ForgetPasswordRequest): Promise<User> {
		const response = await api.post(
			`/user/forget-password`,
			JSON.stringify(body),
			{
				headers: {
					'Content-Type': 'application/json',
					'Accept': 'application/x-protobuf'
				},
				responseType: 'arraybuffer'
			}
		)

		return User.decode(new Uint8Array(response.data))
	}

	async validateToken(body: ValidateThirdPartyTokenRequest): Promise<LoginResponse> {
		const response = await api.post(
			`/auth/oauth/token/validate`,
			JSON.stringify(body),
			{
				headers: {
					'Content-Type': 'application/json',
					'Accept': 'application/json'
				},
				responseType: 'json'
			}
		)

        return response.data as LoginResponse
	}

	logout(): void {
		clearAuthSession()
	}

	async getActiveSessions(): Promise<GetActiveSessionsResponse> {
		const response = await api.post('/auth/sessions', JSON.stringify({}), {
			headers: { Accept: 'application/x-protobuf' },
			responseType: 'arraybuffer',
		})
		return GetActiveSessionsResponse.decode(new Uint8Array(response.data))
	}

	async revokeSession(deviceId: string): Promise<OperationResponse> {
		const body = RevokeSessionRequest.fromPartial({ deviceId })
		const response = await api.post(
			'/auth/sessions/revoke',
			JSON.stringify(RevokeSessionRequest.toJSON(body)),
			{
				headers: { Accept: 'application/x-protobuf' },
				responseType: 'arraybuffer',
			}
		)
		return OperationResponse.decode(new Uint8Array(response.data))
	}
}
